import { useInboxMessageClassificationsQuery } from "@qw/graphql";
import {
  Badge,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@qw/ui";

import { useSetMessageClassification } from "@/providers/use-set-message-classification";

interface MessageClassificationProps {
  messageId: string;
  currentClassification?: string | null;
}

export const MessageClassification = ({ messageId, currentClassification }: MessageClassificationProps) => {
  const { data } = useInboxMessageClassificationsQuery();
  const [setClassification] = useSetMessageClassification();

  const classifications = data?.inboxMessageClassifications ?? [];
  const systemClassifications = classifications.filter((c) => c.source === "System");
  const userClassifications = classifications.filter((c) => c.source === "User");

  const handleSetClassification = (classificationLabel: string) =>
    setClassification({ messageId, classificationLabel });

  const normalizedClassification = currentClassification?.trim().toLowerCase();
  const isUnclassified =
    !currentClassification || normalizedClassification === "unclassified" || currentClassification.trim().length === 0;
  const displayLabel = isUnclassified ? "unclassified" : currentClassification;

  const isChecked = (label: string) => {
    const normalizedLabel = label.trim().toLowerCase();

    if (normalizedLabel === "unclassified" && !currentClassification) {
      return true;
    }

    return label === currentClassification;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Badge
          // NOTE: Need type here because `type` prop is being passed in by `DropdownMenuTrigger` when using `asChild`.
          type="default"
          variant={isUnclassified ? "outline" : "secondary"}
          className="cursor-pointer hover:bg-secondary/80 mt-3"
        >
          {displayLabel}
        </Badge>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {userClassifications.length > 0 && (
          <>
            <DropdownMenuLabel>User Classifications</DropdownMenuLabel>
            {userClassifications.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.id}
                checked={isChecked(c.label)}
                onCheckedChange={() => handleSetClassification(c.label)}
              >
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}

            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuLabel>System Classifications</DropdownMenuLabel>

        {systemClassifications.map((c) => (
          <DropdownMenuCheckboxItem
            key={c.id}
            checked={isChecked(c.label)}
            onCheckedChange={() => handleSetClassification(c.label)}
          >
            {c.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
